import { DataGrid } from '@thought-river/ui-components';
import { ContractsDataGridProps } from './types';
import {
    GridInitialState,
    GridRowParams,
    GridValueGetterParams,
    useGridApiRef,
} from '@mui/x-data-grid-premium';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { useGetColumns, usePersistDataGridState } from './hooks';
import { NegotiationCell } from './cells';
import { ContractMenu } from '@modules/common/components/ContractMenu';
import { ContractInfo, FileFormat } from '@modules/common/types';
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import { Reviewer } from '@thought-river/negotiations-common/dist/api/contractManagement';
import { memo, useCallback, useMemo } from 'react';

const CONTRACTS_DATA_GRID_STATE_LOCAL_STORAGE_KEY = 'contracts_data_grid_state';

const ContractsDataGrid = ({
    data,
    rowSelectionModel,
    onSelectedRowsChange,
    onContractUpdated,
    toolbarLeftContent,
}: ContractsDataGridProps) => {
    const apiRef = useGridApiRef();

    const getTreeDataPath: DataGridProProps['getTreeDataPath'] = useCallback(
        (row) => row.hierarchy,
        []
    );

    const initialState: GridInitialState = useMemo(
        () => ({
            pinnedColumns: {
                right: ['status', 'actions'],
            },
            sorting: { sortModel: [{ field: 'upload_date', sort: 'desc' }] },
            ...JSON.parse(
                localStorage.getItem(
                    CONTRACTS_DATA_GRID_STATE_LOCAL_STORAGE_KEY
                ) || '{}'
            ),
        }),
        []
    );

    const onConfigChange = (config: GridInitialStatePremium) => {
        localStorage.setItem(
            CONTRACTS_DATA_GRID_STATE_LOCAL_STORAGE_KEY,
            JSON.stringify(config)
        );
    };

    const persistDataGridStateProps = usePersistDataGridState({
        onConfigChange,
        apiRef,
    });

    const getActionsColumnActions = useCallback(
        (params: GridRowParams) => {
            const contractInfo: ContractInfo = {
                name: params.row.version_name,
                uuid: params.row.contract_uuid,
                versionUuid: params.row.version.uuid,
                versionNumber: params.row.version.number,
                isLatestVersion: params.row.hierarchy?.length === 1,
                dealTypeName: params.row.deal_type.name,
                dealTypeUuid: params.row.deal_type.uuid,
                contractStatus: params.row.version_status,
                processingStatus: params.row.processing_status,
                documentUuid: params.row.document_uuid,
                fileFormats: [
                    FileFormat.DOCX,
                    ...(params.row.pdf_available ? [FileFormat.PDF] : []),
                ],
                ownerId: params.row.owner.uuid,
                reviewerIds: params.row.reviewers.map((r: Reviewer) => r.uuid),
            };
            return [
                <ContractMenu
                    key={params.id}
                    contractInfo={contractInfo}
                    onContractUpdated={({
                        uuid,
                        contractStatus,
                        processingStatus,
                    }) =>
                        onContractUpdated(
                            uuid,
                            contractStatus,
                            processingStatus
                        )
                    }
                />,
            ];
        },
        [onContractUpdated]
    );

    const columns = useGetColumns(data?.columns ?? [], getActionsColumnActions);

    return (
        <DataGrid
            apiRef={apiRef}
            rows={data?.rows ?? []}
            columns={columns}
            treeData
            groupingColDef={{
                headerName: 'Negotiation',
                disableColumnMenu: false,
                sortable: true,
                filterable: true,
                renderCell: NegotiationCell,
                minWidth: 200,
                flex: 1,
                valueGetter: (params: GridValueGetterParams) =>
                    params.row.version_name,
                aggregable: false,
            }}
            checkboxSelection
            checkboxSelectionVisibleOnly
            isRowSelectable={(params: GridRowParams) =>
                params.row.hierarchy.length === 1
            }
            getTreeDataPath={getTreeDataPath}
            initialState={initialState}
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={onSelectedRowsChange}
            slotProps={{
                toolbar: {
                    copyTableProps: { hide: true },
                    exportProps: { hide: true },
                    leftContent: toolbarLeftContent,
                },
            }}
            {...persistDataGridStateProps}
        />
    );
};

export default memo(ContractsDataGrid);
